// 9fbef606107a605d69c0edbcd8029e5d
import { createSelector } from 'reselect';
import { labelsHashValuesReplace } from '@tcp/core/src/components/features/CnC/LoyaltyBanner/util/utility';
import { SESSIONCONFIG_REDUCER_KEY } from '@tcp/core/src/constants/reducer.constants';
import { getCartOrderDetails } from '../../CartItemTile/container/CartItemTile.selectors';
import ConfirmationSelector from '../../Confirmation/container/Confirmation.selectors';
import {
  getDynamicLabelValues,
  getLabelFromCopyOrFromTranslation,
} from '../../../../../utils/labels/labelUtilty';
import PlaceCashBannerLabels, {
  PLACE_CASH_DYNAMIC_LABELS,
  PLACE_CASH_LABELS,
} from '../views/PlaceCashBanner.labels';

const getCurrentCountry = (state) => {
  return (
    (state[SESSIONCONFIG_REDUCER_KEY] && state[SESSIONCONFIG_REDUCER_KEY].siteDetails.country) ||
    'US'
  );
};

const replaceLabelUtil = ({
  earnedPlaceCashValue,
  cashAmountOff,
  placeCashOffer,
  currency,
  placeCashMultiplier,
}) => {
  return {
    title: [
      {
        key: '#earnedPlaceCashValue#',
        value: `${currency}${earnedPlaceCashValue}`,
      },
    ],
    subTitle: [
      {
        key: '#cashAmountOff#',
        value: `${currency}${cashAmountOff * placeCashMultiplier}`,
      },
      {
        key: '#placeCashOffer#',
        value: `${currency}${placeCashOffer}`,
      },
    ],
  };
};

/** The place cash amount to be rewarded to customer */
const getCashAmountOff = (state) => {
  return (
    (state.session &&
      state.session.siteDetails &&
      state.session.siteDetails.PLACECASH_EARN_AMOUNT_OFF) ||
    0
  );
};

/** Minimum worth of merchandise to be in cart */
const getPlaceCashOffer = (state) => {
  return (
    (state.session &&
      state.session.siteDetails &&
      state.session.siteDetails.PLACECASH_EARN_MIN_PURCHASE) ||
    0
  );
};

const getCurrencySymbol = (state) => {
  const currency = state.session && state.session.siteDetails && state.session.siteDetails.currency;
  // eslint-disable-next-line no-nested-ternary
  return currency ? (currency === 'USD' || currency === 'CAD' ? '$' : currency) : '$';
};

/** Place cash earned on transaction */
const getEarnedPlaceCashValue = (state) => {
  return (
    getCartOrderDetails(state).get('rewardsToBeEarned') ||
    ConfirmationSelector.getEarnedPlaceCashValue(state)
  );
};

const getPlaceDetailsContentInfo = createSelector(
  (arg, labelName) => labelName,
  (labelName) => {
    const { referred } = getDynamicLabelValues('checkout.placeCashBanner');
    return referred && referred.length && referred.find((label) => label.name === labelName);
  }
);

const getAirmilesBannerContentInfo = () => {
  const { referred } = getDynamicLabelValues('global.airmilesBanner');
  return referred && referred.length && referred.find((label) => label.name === 'air_miles_banner');
};

/** Flag indicated whether the place cash is enabled  */
const getIsNotInternationalShipping = (currentCountry) => {
  return currentCountry === 'US' || currentCountry === 'CA';
};

const getIsPlaceCashEnabled = (state) => {
  const currentCountry = getCurrentCountry(state);
  const earnedPlaceCashValue = getEarnedPlaceCashValue(state);
  return getIsNotInternationalShipping(currentCountry) && earnedPlaceCashValue > 0;
};

const getModuleX = (state) => state.CartPageReducer.get('moduleXContent');

const getCurrentPageName = (isOrderConfrimation) => {
  return isOrderConfrimation ? 'confirmation' : 'bag';
};

const getPlaceCashDetailBannerLabel = createSelector(
  getCurrentCountry,
  getCurrentPageName,
  (country, page) => {
    return `PlaceCash_Detail_${country}_${page}`;
  }
);

const getPlaceDetailsContent = createSelector(
  [getPlaceCashDetailBannerLabel, getModuleX],
  (labelName, moduleXContent) => {
    const { referred } = getDynamicLabelValues('checkout.placeCashBanner');
    const refObj =
      referred && referred.length && referred.find((label) => label.name === labelName);
    const showDetailsContent = moduleXContent.find((moduleX) => moduleX.name === refObj?.name);
    return showDetailsContent && showDetailsContent.richText;
  }
);

const getPlaceCashMultiplier = (state) => {
  return (
    (state.session &&
      state.session.siteDetails &&
      !Number.isNaN(state.session.siteDetails.PLACECASH_BONUS_VOUCHER_MULTIPLIER) &&
      parseFloat(state.session.siteDetails.PLACECASH_BONUS_VOUCHER_MULTIPLIER)) ||
    1
  );
};

const getIsShowBanner = (state) => {
  return +(
    (state.session &&
      state.session.siteDetails &&
      state.session.siteDetails.PLACECASH_BONUS_VOUCHER_MULTIPLIER) ||
    0
  );
};

const getStaticLabelKeyValue = (key) => {
  return getLabelFromCopyOrFromTranslation(`${PLACE_CASH_LABELS}.${key}`, PlaceCashBannerLabels);
};

const getPlaceCashBannerLabels = createSelector(
  [
    (state, isOrderConfirmation) => {
      return isOrderConfirmation;
    },
    getIsShowBanner,
    getCurrentCountry,
    getCurrencySymbol,
    getCashAmountOff,
    getEarnedPlaceCashValue,
    getPlaceCashOffer,
    getPlaceCashMultiplier,
    getPlaceDetailsContent,
  ],
  // eslint-disable-next-line max-params
  (
    isOrderConfirmation,
    isEnabled,
    currentCountry,
    currency,
    cashAmountOff,
    earnedPlaceCashValue,
    placeCashOffer,
    placeCashMultiplier,
    placeCashDetailBannerLabel
    // eslint-disable-next-line max-params
  ) => {
    const staticlabels = [
      'subTitle',
      'tnc',
      'modalLink',
      'placeRewardLinkTitle',
      'placeRewardModelHeader',
      'placeRewardModelDesc',
      'title',
      'altName',
      'detailModalTitle',
    ];
    const dynamiclabels = ['imgUrl', 'subTitle'];
    const finalValue = {};
    if (isEnabled) {
      const currentPage = getCurrentPageName(isOrderConfirmation);
      const replaceLabelConfig = replaceLabelUtil({
        currency,
        cashAmountOff,
        earnedPlaceCashValue,
        placeCashOffer,
        placeCashMultiplier,
      });

      const staticLabelKeys = [
        `lbl_placeCash_${currentCountry}_bag_subTitle`,
        `lbl_placeCash_${currentCountry}_${currentPage}_tnc`,
        `lbl_placeCash_${currentCountry}_${currentPage}_modalLink`,
        `lbl_placereward_link_title`,
        `lbl_placereward_model_header`,
        `lbl_placereward_model_description`,
        `lbl_placeCash_${currentCountry}_${currentPage}_title`,
        `lbl_placeCash_${currentCountry}_${currentPage}_altName`,
        `lbl_placeCash_${currentCountry}_${currentPage}_detailModalTitle`,
      ];
      const dynamicLabelKeys = [
        `lbl_placeCash_${currentCountry}_${currentPage}_imgUrl`,
        `lbl_placeCash_${currentCountry}_confirmation_subTitle`,
      ];

      staticLabelKeys.forEach((key, index) => {
        const labelKeyValue = getStaticLabelKeyValue(key);
        const labelString = labelKeyValue === key ? '' : labelKeyValue;
        const labelConfig = replaceLabelConfig[staticlabels[index]];

        finalValue[staticlabels[index]] = labelConfig
          ? labelsHashValuesReplace(labelString, labelConfig)
          : labelString;
      });
      dynamicLabelKeys.forEach((key, index) => {
        const labelKeyValue = getDynamicLabelValues(`${PLACE_CASH_DYNAMIC_LABELS}.${key}`);

        const labelString = labelKeyValue === key ? '' : labelKeyValue;
        const labelConfig = replaceLabelConfig[dynamiclabels[index]];

        finalValue[dynamiclabels[index]] = labelConfig
          ? labelsHashValuesReplace(labelString, labelConfig)
          : labelString;
      });
      finalValue.SHOW_DETAILS_RICH_TEXT = placeCashDetailBannerLabel;
    } else {
      staticlabels.forEach((key) => {
        finalValue[key] = '';
      });
      dynamiclabels.forEach((key) => {
        finalValue[key] = '';
      });
    }
    return finalValue;
  }
);

export default {
  getIsPlaceCashEnabled,
  getPlaceDetailsContentInfo,
  getAirmilesBannerContentInfo,
  getPlaceCashBannerLabels,
  getPlaceCashDetailBannerLabel,
  getCashAmountOff,
  getPlaceCashOffer,
  getCurrentCountry,
  getIsShowBanner,
};
